import { Intent } from "@blueprintjs/core";
import {
  ApiClientType,
  MARKETING_SITE_LINKS,
  NotificationDisplayTitleMetadata,
  OrgRole,
  ReviewDeterminiation,
  ReviewRequestResult,
  RunNotificationRunStatus,
  getDisplaySafeToken,
  getSimplifiedNotificationTitle,
  guardNever,
  humanReadableOrgRole,
  notEmpty,
} from "@hex/common";
import React from "react";
import styled from "styled-components";

import { Link } from "../common/DocsLink.js";

const BoldText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.MEDIUM};
`;

const IntentText = styled.span<{ $intent: Intent }>`
  color: ${({ $intent, theme }) =>
    `${theme.button.default[$intent].fontColor}`};
`;

export function getNotificationTitle({
  displayName,
  exploreRoleVisibleButDisabled,
  link,
  titleMetadata,
}: {
  titleMetadata: NotificationDisplayTitleMetadata;
  displayName: string;
  // Temporary org-role request specific args
  exploreRoleVisibleButDisabled: boolean;
  link: string;
}): React.ReactNode {
  const type = titleMetadata.type;
  switch (type) {
    case "COMMENT": {
      const { commentType, hexTitle, isMention } = titleMetadata;
      if (isMention) {
        return (
          <>
            <BoldText>{displayName}</BoldText> mentioned you in a comment on{" "}
            <BoldText>{hexTitle}</BoldText>
          </>
        );
      }
      switch (commentType) {
        case "CELL":
          return (
            <>
              <BoldText>{displayName}</BoldText> commented on{" "}
              <BoldText>{hexTitle}</BoldText>
            </>
          );
        case "COMMENT":
          return (
            <>
              <BoldText>{displayName}</BoldText> replied to a comment on{" "}
              <BoldText>{hexTitle}</BoldText>
            </>
          );
        default:
          guardNever(commentType, commentType);
      }
      break;
    }
    case "TEXT_CELL_MENTION":
      return (
        <>
          <BoldText>{displayName}</BoldText> mentioned you in{" "}
          <BoldText>{titleMetadata.hexTitle}</BoldText>
        </>
      );
    case "HEX_ROLE_REQUEST": {
      let content: JSX.Element;
      if (titleMetadata.hexAccessEntity.includes("thread")) {
        content = (
          <>
            <BoldText>{displayName}</BoldText> wants access to view the thread,{" "}
            <BoldText>{titleMetadata.hexTitle}</BoldText>
          </>
        );
      } else {
        content = (
          <>
            <BoldText>{displayName}</BoldText> has requested{" "}
            {titleMetadata.accessType} to{" "}
            <BoldText>{titleMetadata.hexTitle}</BoldText>
          </>
        );
      }
      if (titleMetadata.isResolved) {
        return <s>{content}</s>;
      }
      return content;
    }
    case "ADMIN_HEX_ROLE_GRANTED":
      return (
        <>
          <BoldText>{displayName}</BoldText> granted themselves{" "}
          {titleMetadata.accessType} to {titleMetadata.hexAccessEntity}{" "}
          <BoldText>{titleMetadata.hexTitle}</BoldText>
        </>
      );
    case "ORG_ROLE_REQUEST":
      {
        // Temporary logic for Explorer seat rollout: if the user is requesting
        // an Explorer seat, and Explorer seats are not yet enabled, show a
        // special message with a link to contact sales.
        if (
          exploreRoleVisibleButDisabled &&
          titleMetadata.orgRole === OrgRole.EXPLORER
        ) {
          const contactSalesClick = (e: React.MouseEvent) => {
            // Don't allow the outer link to navigate
            e.preventDefault();
            e.stopPropagation();
            window.open(MARKETING_SITE_LINKS.RequestExplore, "_blank");
          };
          const upgradeClick = (e: React.MouseEvent) => {
            // Don't allow the outer link to navigate
            e.preventDefault();
            e.stopPropagation();
            window.open(link, "_blank");
          };

          return (
            <>
              <BoldText>{displayName}</BoldText> has requested the workspace
              role of{" "}
              <BoldText>{humanReadableOrgRole(titleMetadata.orgRole)}</BoldText>
              . <Link onClick={contactSalesClick}>Contact sales</Link> to enable{" "}
              {humanReadableOrgRole(OrgRole.EXPLORER)} seats or{" "}
              <Link onClick={upgradeClick}>
                upgrade the user to an Editor seat
              </Link>
              .
            </>
          );
        }
      }
      return (
        <>
          <BoldText>{displayName}</BoldText> has requested the workspace role of{" "}
          <BoldText>{humanReadableOrgRole(titleMetadata.orgRole)}</BoldText>
        </>
      );
    case "SCHEDULED_RUN_SUCCESS":
      return getSimplifiedNotificationTitle({
        projectName: titleMetadata.hexTitle,
        runStatus: RunNotificationRunStatus.SUCCEEDED,
      });
    case "SCHEDULED_RUN_FAILURE":
      return getSimplifiedNotificationTitle({
        projectName: titleMetadata.hexTitle,
        runStatus:
          titleMetadata.issue === "timed out"
            ? RunNotificationRunStatus.TIMED_OUT
            : RunNotificationRunStatus.FAILED,
      });
    case "INITIAL_TOKEN_EXPIRATION":
    case "FINAL_TOKEN_EXPIRATION": {
      const tokenType =
        titleMetadata.apiClientType === ApiClientType.WORKSPACE_TOKEN
          ? "workspace"
          : "personal access";
      const tokenIdentifier = getDisplaySafeToken(
        titleMetadata.apiClientType,
        titleMetadata.lastFour,
      );
      const tokenDetails = [
        tokenIdentifier,
        titleMetadata.description ? titleMetadata.description : null,
      ]
        .filter(notEmpty)
        .join(" - ");

      return (
        <>
          Your {tokenType} token
          {tokenDetails && (
            <>
              {" "}
              (<BoldText>{tokenDetails}</BoldText>)
            </>
          )}{" "}
          is expiring in {titleMetadata.tokenExpirationHours} hours
        </>
      );
    }
    case "INITIAL_OAUTH_TOKEN_EXPIRATION":
    case "FINAL_OAUTH_TOKEN_EXPIRATION":
      return (
        <>
          Your <BoldText>{titleMetadata.connectionType}</BoldText> connection
          for <BoldText>{titleMetadata.accountName}</BoldText> is expiring soon
        </>
      );
    case "CONNECTED_APP":
      return (
        <>
          <BoldText>{titleMetadata.app}</BoldText> has been connected to your
          workspace
        </>
      );
    case "PROJECT_SHARE": {
      const isThread = titleMetadata.hexAccessEntity.includes("thread");
      if (isThread) {
        return (
          <>
            <BoldText>{displayName}</BoldText> shared a thread with you:{" "}
            <BoldText>{titleMetadata.hexTitle}</BoldText>
          </>
        );
      }
      return (
        <>
          <BoldText>{displayName}</BoldText> shared{" "}
          {titleMetadata.hexAccessEntity}{" "}
          <BoldText>{titleMetadata.hexTitle}</BoldText> with you
        </>
      );
    }
    case "ARCHIVE_DIGEST": {
      if (
        titleMetadata.numArchived === 1 &&
        titleMetadata.archivedName != null
      ) {
        return (
          <>
            <BoldText>The project {titleMetadata.archivedName}</BoldText> was
            automatically archived
          </>
        );
      } else if (titleMetadata.numArchived === 1) {
        return (
          <>
            <BoldText>1 project</BoldText> was automatically archived
          </>
        );
      } else if (titleMetadata.archivedName != null) {
        return (
          <>
            <BoldText>
              {titleMetadata.archivedName} and {titleMetadata.numArchived - 1}{" "}
              other projects
            </BoldText>{" "}
            were automatically archived
          </>
        );
      } else {
        return (
          <>
            <BoldText>{titleMetadata.numArchived} projects</BoldText> were
            automatically archived
          </>
        );
      }
    }
    case "PROJECT_LIFECYCLE": {
      const action =
        titleMetadata.event === "ARCHIVED" ? "archived" : "trashed";
      if (
        titleMetadata.numActioned === 1 &&
        titleMetadata.projectName != null
      ) {
        return (
          <>
            <BoldText>{titleMetadata.actorName}</BoldText> {action} the project{" "}
            <BoldText>{titleMetadata.projectName}</BoldText>
          </>
        );
      } else if (titleMetadata.numActioned === 1) {
        return (
          <>
            <BoldText>{titleMetadata.actorName}</BoldText> {action}{" "}
            <BoldText>1 project</BoldText>
          </>
        );
      } else if (titleMetadata.projectName != null) {
        return (
          <>
            <BoldText>{titleMetadata.actorName}</BoldText> {action}{" "}
            <BoldText>
              {titleMetadata.projectName} and {titleMetadata.numActioned - 1}{" "}
              other projects
            </BoldText>
          </>
        );
      } else {
        return (
          <>
            <BoldText>{titleMetadata.actorName}</BoldText> {action}{" "}
            <BoldText>{titleMetadata.numActioned} projects</BoldText>
          </>
        );
      }
    }
    case "REVIEW_REQUESTED": {
      const actionString = titleMetadata.reset ? "re-requested" : "requested";
      let body: string | JSX.Element =
        `Your review has been ${actionString} on `;
      if (titleMetadata.requestorName != null) {
        if (titleMetadata.reviewerGroupName != null) {
          body = (
            <>
              <BoldText>{titleMetadata.requestorName}</BoldText> {actionString}{" "}
              review from your group {titleMetadata.reviewerGroupName} on{" "}
            </>
          );
        } else {
          body = (
            <>
              <BoldText>{titleMetadata.requestorName}</BoldText> {actionString}{" "}
              your review on{" "}
            </>
          );
        }
      }

      return (
        <>
          {body}
          <BoldText>{titleMetadata.hexTitle}</BoldText>
        </>
      );
    }

    case "REVIEW_CREATED": {
      const actionString = {
        [ReviewDeterminiation.COMMENTED]: "commented on",
        [ReviewDeterminiation.APPROVED]: "approved",
        [ReviewDeterminiation.DENIED]: "requested changes to",
      }[titleMetadata.determination];

      const actionIntent = {
        [ReviewDeterminiation.COMMENTED]: Intent.NONE,
        [ReviewDeterminiation.APPROVED]: Intent.SUCCESS,
        [ReviewDeterminiation.DENIED]: Intent.WARNING,
      }[titleMetadata.determination];

      return (
        <>
          <BoldText>{titleMetadata.reviewerName}</BoldText>{" "}
          <IntentText $intent={actionIntent}>{actionString}</IntentText> your
          review request on <BoldText>{titleMetadata.hexTitle}</BoldText>
        </>
      );
    }
    case "REVIEW_REQUEST_RESOLVED": {
      const actionString = {
        [ReviewRequestResult.ACCEPTED]: "published",
        [ReviewRequestResult.CANCELLED]: "closed",
        [ReviewRequestResult.PENDING]: "reopened",
      }[titleMetadata.result];

      const actionIntent = {
        [ReviewRequestResult.ACCEPTED]: Intent.SUCCESS,
        [ReviewRequestResult.CANCELLED]: Intent.DANGER,
        [ReviewRequestResult.PENDING]: Intent.NONE,
      }[titleMetadata.result];

      return (
        <>
          <BoldText>{titleMetadata.resolverName}</BoldText>{" "}
          <IntentText $intent={actionIntent}>{actionString}</IntentText> your
          review request on <BoldText>{titleMetadata.hexTitle}</BoldText>
        </>
      );
    }
    case "COMPONENT_UPGRADE_AVAILABLE": {
      return titleMetadata.numHexesToUpgrade > 1 ? (
        <>
          A new version of{" "}
          <BoldText>{titleMetadata.componentName ?? "a component"}</BoldText> is
          available. Upgrade {titleMetadata.numHexesToUpgrade} projects to stay
          up to date.
        </>
      ) : (
        <>
          A new version of{" "}
          <BoldText>{titleMetadata.componentName ?? "a component"}</BoldText> is
          available. Upgrade <BoldText>{titleMetadata.firstHexTitle}</BoldText>{" "}
          to stay up to date.
        </>
      );
    }
    case "COMPONENT_IMPORT_CELL_UPDATED": {
      return titleMetadata.numHexesUpdated > 1 ? (
        <>
          <BoldText>{titleMetadata.numHexesUpdated} projects</BoldText> have
          been updated to use a new version of{" "}
          <BoldText>{titleMetadata.componentName}</BoldText>.
        </>
      ) : (
        <>
          <BoldText>{titleMetadata.firstHexTitle}</BoldText> has been updated to
          use a new version of{" "}
          <BoldText>{titleMetadata.componentName}</BoldText>.
        </>
      );
    }
    default:
      guardNever(type, type);
  }
  return null;
}
